import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { PoModalAction, PoModalComponent, PoTableColumn, PoTagType } from '@po-ui/ng-components';
import { PoCodeEditorComponent } from '@po-ui/ng-code-editor';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ReviewAnswer } from '../../interfaces/review-answer.interface';
import { ReviewFeedback } from '../../interfaces/review-feedback.interface';
import { ReviewFeedbackChange } from '../review-feedback/review-feedback.component';
import { DiffPair } from '../../utils/diff-parser.util';
import { ChangesetFreshness, FreshnessChange } from '../../utils/changeset-freshness.util';
import { ensureAdvplLanguageRegistered } from '../../utils/monaco-advpl-language.util';
import {
  formatSeverityLabel,
  formatSonarIssueType,
  formatSonarSeverity,
  severityKey,
  toTitle
} from '../../utils/severity.util';

interface MonacoAnnotation {
  line: number;
  severity: 'high' | 'medium' | 'low';
  kind: 'required' | 'suggestion';
  hoverMessage: string;
}

interface MonacoDisposable {
  dispose(): void;
}

@Component({
  selector: 'app-review-monaco-viewer',
  templateUrl: './review-monaco-viewer.component.html',
  styleUrls: ['./review-monaco-viewer.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewMonacoViewerComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() isStandalone = false;
  // Quando embutido em um po-modal (ex.: "Review original"), o viewer preenche a
  // altura do corpo do modal: a toolbar fica fixa no topo e apenas o editor rola
  // internamente (igual ao modo tela cheia), sem gerar barra de rolagem no modal.
  @Input() fitContainer = false;
  @Input() answer: ReviewAnswer | null = null;
  @Input() diffPair: DiffPair = {
    original: '',
    modified: '',
    language: 'plaintext',
    lineCount: 0,
    stats: { added: 0, removed: 0 },
    hunks: [],
    isAddedFile: false
  };
  @Input() standaloneSource = '';
  @Input() sourceLanguage = 'plaintext';
  @Input() loading = false;
  @Input() showWorkspaceActions = true;
  @Input() annotationsPanelCollapsed = false;
  @Input() activeAnnotationLine: number | null = null;
  @Input() resourceType = '';
  @Input() resourceId = '';
  @Input() changesetContextLabel = '';
  @Input() changesetFreshness: ChangesetFreshness | null = null;
  @Input() answerArtifactId?: number;
  /**
   * Mensagem exibida no lugar dos achados quando o review ainda não tem análise.
   *
   * Ocupa a lateral direita, e não um bloco abaixo do diff, porque é ali que os achados
   * aparecem: dizer no lugar deles ensina onde procurar e mantém a mesma geometria de tela
   * com e sem análise. Vazio mantém o diff em largura cheia.
   */
  @Input() emptyAnalysisMessage = '';

  /**
   * Se a análise tem algo para a lateral listar. Sem achados, a lateral só diria "nenhuma
   * ação ou sugestão" ocupando ¼ da largura — a Visão Geral já diz que o arquivo passou.
   */
  get hasFindings(): boolean {
    return !!(this.answer?.required_actions?.length || this.answer?.suggestions?.length);
  }

  /** A lateral com os achados, quando há achados e o usuário não a recolheu. */
  get showFindingsPanel(): boolean {
    return this.hasFindings && !this.annotationsPanelCollapsed;
  }

  /** Se a lateral direita deve aparecer — com os achados ou com a mensagem que os substitui. */
  get showSidePanel(): boolean {
    return this.showFindingsPanel || (!this.answer && !!this.emptyAnalysisMessage);
  }
  @Input() feedbackByFindingId = new Map<string, ReviewFeedback>();
  @Input() otherFeedbackByFindingId = new Map<string, ReviewFeedback[]>();

  @Output() annotationsPanelCollapsedChange = new EventEmitter<boolean>();
  @Output() activeAnnotationLineChange = new EventEmitter<number | null>();
  @Output() feedbackChanged = new EventEmitter<ReviewFeedbackChange>();
  @Output() otherFeedbackRemoved = new EventEmitter<ReviewFeedback>();

  @ViewChild('diffEditor') private diffEditorRef!: PoCodeEditorComponent;
  @ViewChild('sourceEditor') private sourceEditorRef?: PoCodeEditorComponent;

  diffInline = false;
  canUseSideBySide = true;
  showActions = true;
  showSuggestions = true;
  isFullscreen = false;
  private readonly sideBySideMinWidth = 1280;
  private readonly toolbarCompactWithSideThreshold = 1840;
  private readonly toolbarUltraCompactWithSideThreshold = 1680;
  private readonly toolbarCompactThreshold = 1520;
  private readonly toolbarUltraCompactThreshold = 1360;
  private preferredDesktopInline = false;
  private forcedInlineByViewport = false;

  private monacoDecorationIds: string[] = [];
  private cursorChangeDisposable: MonacoDisposable | null = null;
  private mouseDownDisposable: MonacoDisposable | null = null;
  private resizeReflowTimer: ReturnType<typeof setTimeout> | null = null;
  private deferredAnnotationTimers: Array<ReturnType<typeof setTimeout>> = [];
  private reflowGeneration = 0;
  private readonly initialReflowDelayMs = 200;
  private readonly reflowRetryDelayMs = 150;
  private readonly maxReflowRetries = 8;
  private readonly fullscreenEditorMinHeight = 260;
  private readonly fullscreenEditorFallbackOffset = 220;
  private readonly fullscreenEditorBottomGap = 12;
  private containerResizeObserver: ResizeObserver | null = null;
  private lastContainerWidth = 0;
  private destroy$ = new Subject<void>();
  private diffNavigatorActivated = false;
  private viewReady = false;

  diffChangeIndex = 0;
  diffChangeCount = 0;
  sidePanelHeight = 0;
  toolbarCompact = false;
  toolbarUltraCompact = false;

  @ViewChild('freshnessModal') freshnessModal?: PoModalComponent;

  /** Linhas do modal do que mudou. */
  freshnessRows: FreshnessChange[] = [];
  freshnessRowsLoading = false;
  /** Até aqui a tabela cresce com as linhas; acima, ganha altura fixa e rola por dentro. */
  readonly freshnessTableMaxRows = 8;

  /** Colunas da lista do que mudou; o rótulo do id segue a origem (changeset ou commit). */
  freshnessColumns: PoTableColumn[] = this.buildFreshnessColumns('changeset');

  /** Cabeçalho do modal: "Changeset revisado" no TFVC, "Commit revisado" no git. */
  freshnessReviewedTitle(freshness: ChangesetFreshness): string {
    return freshness.unit === 'commit' ? 'Commit revisado' : 'Changeset revisado';
  }

  private buildFreshnessColumns(unit: 'changeset' | 'commit'): PoTableColumn[] {
    return [
      { property: 'id', label: unit === 'commit' ? 'Commit' : 'Changeset', width: '110px' },
      { property: 'when', label: 'Data', width: '130px' },
      { property: 'author', label: 'Autor', width: '280px' },
      // Quebra em várias linhas e mantém as quebras do próprio comentário (CSS
      // .sr-freshness-table): cortado numa linha só, o texto longo esticava a tabela além
      // do modal e empurrava as outras colunas para fora.
      { property: 'message', label: 'Comentário' }
    ];
  }

  readonly freshnessModalClose: PoModalAction = {
    label: 'Fechar',
    action: () => this.freshnessModal?.close()
  };

  /**
   * Desatualizado, o clique no selo abre o que mudou; nos outros estados não há o que abrir.
   *
   * O modal abre com a tabela em carregamento e só depois recebe as linhas: montar a
   * po-table junto com a abertura deixava o modal em branco até ela terminar.
   */
  openFreshnessDetails(): void {
    const newer = this.changesetFreshness?.newer;
    if (!newer?.length) { return; }
    this.freshnessColumns = this.buildFreshnessColumns(this.changesetFreshness?.unit ?? 'changeset');
    this.freshnessRows = [];
    this.freshnessRowsLoading = true;
    this.freshnessModal?.open();
    setTimeout(() => {
      this.freshnessRows = newer;
      this.freshnessRowsLoading = false;
      this.cdr.markForCheck();
    });
  }

  freshnessReviewedLabel(freshness: ChangesetFreshness): string {
    const reviewed = freshness.reviewed;
    return reviewed ? [reviewed.id, reviewed.when, reviewed.author].filter(Boolean).join(' · ') : '';
  }

  /** Tipo do po-tag do selo; as cores finais vêm das classes sr-freshness-tag--*, como nos contadores. */
  freshnessTagType(freshness: ChangesetFreshness): PoTagType {
    if (freshness.state === 'current') { return PoTagType.Success; }
    if (freshness.state === 'outdated') { return PoTagType.Warning; }
    return PoTagType.Neutral;
  }
  constructor(
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private hostRef: ElementRef<HTMLElement>
  ) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.loadCardVisibility();
    this.ensureCustomMonacoLanguages();
    this.observeContainerResize();
    this.preferredDesktopInline = this.diffInline;
    this.refreshToolbarDensity();
    this.refreshResponsiveDiffMode();
    this.scheduleEditorReflow();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isStandalone'] && !changes['isStandalone'].firstChange) {
      this.refreshResponsiveDiffMode();
    }

    if (changes['answer'] || changes['annotationsPanelCollapsed'] || changes['isStandalone']) {
      this.refreshToolbarDensity();
    }

    if (changes['activeAnnotationLine'] && !changes['activeAnnotationLine'].firstChange) {
      this.cdr.markForCheck();
    }

    if (!this.viewReady) { return; }

    const shouldReflow =
      !!changes['answer'] ||
      !!changes['diffPair'] ||
      !!changes['standaloneSource'] ||
      !!changes['sourceLanguage'] ||
      !!changes['isStandalone'] ||
      !!changes['annotationsPanelCollapsed'] ||
      (!!changes['loading'] && !this.loading);

    if (shouldReflow && !this.loading) {
      this.scheduleEditorReflow();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.containerResizeObserver?.disconnect();
    this.containerResizeObserver = null;
    try { this.cursorChangeDisposable?.dispose?.(); } catch { /* */ }
    try { this.mouseDownDisposable?.dispose?.(); } catch { /* */ }
    this.clearDeferredAnnotationTimers();
    if (this.resizeReflowTimer) { clearTimeout(this.resizeReflowTimer); }
    if (this.isFullscreen && typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  get requiredActionsCount(): number {
    const summaryIssues = this.answer?.summary?.issues;
    if (typeof summaryIssues === 'number') {
      return summaryIssues;
    }
    return this.answer?.required_actions?.length ?? 0;
  }

  get suggestionsCount(): number {
    const summarySuggestions = this.answer?.summary?.suggestions;
    if (typeof summarySuggestions === 'number') {
      return summarySuggestions;
    }
    return this.answer?.suggestions?.length ?? 0;
  }

  get sonarRulesCount(): number {
    const rules = (this.answer?.required_actions ?? [])
      .map(action => (action.sonar_rule_code ?? '').trim())
      .filter(Boolean);
    return new Set(rules).size;
  }

  get actionsCounterLabel(): string {
    return `${this.showActions ? 'Ações' : 'Ações ocultas'}: ${this.requiredActionsCount}`;
  }

  get suggestionsCounterLabel(): string {
    return `${this.showSuggestions ? 'Sugestões' : 'Sugestões ocultas'}: ${this.suggestionsCount}`;
  }

  get actionsCounterTooltip(): string {
    return this.showActions ? 'Clique para ocultar ações' : 'Clique para mostrar ações';
  }

  get suggestionsCounterTooltip(): string {
    return this.showSuggestions ? 'Clique para ocultar sugestões' : 'Clique para mostrar sugestões';
  }

  get sideBySideLabel(): string {
    return this.toolbarUltraCompact ? 'Lado' : 'Lado a lado';
  }

  get annotationsToggleLabel(): string {
    if (this.toolbarUltraCompact) {
      return this.annotationsPanelCollapsed ? 'Mostrar painel' : 'Ocultar painel';
    }
    if (this.toolbarCompact) {
      return this.annotationsPanelCollapsed ? 'Mostrar ações' : 'Ocultar ações';
    }
    return this.annotationsPanelCollapsed ? 'Mostrar ações e sugestões' : 'Ocultar ações e sugestões';
  }

  get expandButtonLabel(): string {
    if (this.toolbarCompact) { return ''; }
    return this.isFullscreen ? 'Contrair' : 'Expandir';
  }

  get expandButtonKind(): 'secondary' | 'tertiary' {
    return this.toolbarCompact ? 'secondary' : 'tertiary';
  }

  private setActiveAnnotationLine(line: number | null): void {
    if (this.activeAnnotationLine === line) { return; }
    this.activeAnnotationLine = line;
    this.activeAnnotationLineChange.emit(line);
  }

  toggleAnnotationsPanel(): void {
    this.annotationsPanelCollapsedChange.emit(!this.annotationsPanelCollapsed);
  }

  toggleActions(): void {
    this.showActions = !this.showActions;
    this.saveCardVisibility();
    this.cdr.markForCheck();
  }

  toggleSuggestions(): void {
    this.showSuggestions = !this.showSuggestions;
    this.saveCardVisibility();
    this.cdr.markForCheck();
  }

  showAllAnnotations(): void {
    this.showActions = true;
    this.showSuggestions = true;
    this.saveCardVisibility();
    this.cdr.markForCheck();
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    if (typeof document !== 'undefined') {
      document.body.style.overflow = this.isFullscreen ? 'hidden' : '';
    }
    this.cdr.markForCheck();
    this.refreshToolbarDensity();
    this.scheduleEditorReflow();
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey(): void {
    if (this.isFullscreen) {
      this.toggleFullscreen();
    }
  }

  private loadCardVisibility(): void {
    try {
      const raw = localStorage.getItem('smartReview.cardVisibility');
      if (raw) {
        const prefs = JSON.parse(raw);
        this.showActions = prefs.showActions ?? true;
        this.showSuggestions = prefs.showSuggestions ?? true;
      }
    } catch { /* ignora */ }
  }

  private saveCardVisibility(): void {
    localStorage.setItem('smartReview.cardVisibility',
      JSON.stringify({ showActions: this.showActions, showSuggestions: this.showSuggestions }));
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    this.refreshResponsiveDiffMode();
    this.refreshToolbarDensity();
    this.scheduleEditorReflow();
  }

  get diffHeight(): number {
    if (this.isFullscreen && typeof window !== 'undefined') {
      return this.getFullscreenEditorHeight();
    }
    if (this.fitContainer && typeof window !== 'undefined') {
      return this.getFitContainerEditorHeight();
    }
    const lines = this.isStandalone
      ? (this.standaloneSource ? this.standaloneSource.split('\n').length : 0)
      : (this.diffPair.lineCount || this.diffPair.modified.split('\n').length);
    const lineHeightPx = 22;
    const contentHeight = lines * lineHeightPx + 80;
    const minHeight = this.isStandalone ? 540 : 500;
    const viewportCap = typeof window !== 'undefined'
      ? Math.floor(window.innerHeight * 0.78)
      : 800;
    const hardMax = this.isStandalone ? 1000 : 900;
    const maxHeight = Math.min(hardMax, viewportCap);
    return Math.min(Math.max(contentHeight, minHeight), Math.max(minHeight, maxHeight));
  }

  private getFullscreenEditorHeight(): number {
    const fallbackHeight = Math.max(
      this.fullscreenEditorMinHeight,
      window.innerHeight - this.fullscreenEditorFallbackOffset
    );

    if (!this.viewReady) {
      return fallbackHeight;
    }

    const editorHost = this.hostRef?.nativeElement?.querySelector(
      '.sr-diff-workspace--fullscreen .sr-diff-surface po-code-editor'
    ) as HTMLElement | null;

    if (!editorHost) {
      return fallbackHeight;
    }

    const top = editorHost.getBoundingClientRect().top;
    if (!Number.isFinite(top)) {
      return fallbackHeight;
    }

    const availableHeight = Math.floor(window.innerHeight - top - this.fullscreenEditorBottomGap);
    if (!Number.isFinite(availableHeight) || availableHeight <= 0) {
      return fallbackHeight;
    }

    return Math.max(this.fullscreenEditorMinHeight, availableHeight);
  }

  // Altura do editor quando embutido em um po-modal: preenche o espaço restante do
  // corpo do modal (após a toolbar), de modo que o conteúdo caiba exatamente na
  // altura máxima do modal e apenas o editor role internamente (sem scroll no modal).
  private getFitContainerEditorHeight(): number {
    const fallbackHeight = Math.max(
      this.fullscreenEditorMinHeight,
      Math.floor(window.innerHeight * 0.6)
    );

    if (!this.viewReady) {
      return fallbackHeight;
    }

    const editorHost = this.hostRef?.nativeElement?.querySelector(
      '.sr-diff-surface po-code-editor'
    ) as HTMLElement | null;
    if (!editorHost) {
      return fallbackHeight;
    }

    const modalBody = editorHost.closest('.po-modal-body') as HTMLElement | null;
    if (!modalBody) {
      // Não está dentro de um modal: usa fallback conservador.
      return fallbackHeight;
    }

    const editorTop = editorHost.getBoundingClientRect().top;
    if (!Number.isFinite(editorTop)) {
      return fallbackHeight;
    }

    const bodyRect = modalBody.getBoundingClientRect();
    const bodyStyle = window.getComputedStyle(modalBody);
    const borderTop = parseFloat(bodyStyle.borderTopWidth) || 0;
    const borderBottom = parseFloat(bodyStyle.borderBottomWidth) || 0;
    const paddingTop = parseFloat(bodyStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(bodyStyle.paddingBottom) || 0;

    // Usa a altura MÁXIMA do corpo do modal como limite fixo. Basear em clientHeight
    // (altura atual) causaria encolhimento progressivo a cada recálculo, pois o corpo
    // não tem altura fixa e é centralizado verticalmente.
    const maxHeight = parseFloat(bodyStyle.maxHeight);
    let bodyContentHeight: number;
    if (Number.isFinite(maxHeight) && maxHeight > 0) {
      bodyContentHeight = bodyStyle.boxSizing === 'border-box'
        ? maxHeight - borderTop - borderBottom - paddingTop - paddingBottom
        : maxHeight;
    } else {
      // Sem max-height definido: cai para a altura atual do conteúdo.
      bodyContentHeight = modalBody.clientHeight - paddingTop - paddingBottom;
    }

    // Offset do topo do editor dentro da área de conteúdo do corpo (toolbar + margens +
    // paddings acima). Estável, pois esses elementos têm altura fixa.
    const bodyContentTop = bodyRect.top + borderTop + paddingTop;
    const offsetAboveEditor = editorTop - bodyContentTop;

    const surface = editorHost.closest('.sr-diff-surface') as HTMLElement | null;
    const surfacePaddingBottom = surface
      ? (parseFloat(window.getComputedStyle(surface).paddingBottom) || 0)
      : 0;

    const availableHeight = Math.floor(
      bodyContentHeight - offsetAboveEditor - surfacePaddingBottom - this.fullscreenEditorBottomGap
    );
    if (!Number.isFinite(availableHeight) || availableHeight <= 0) {
      return fallbackHeight;
    }

    return Math.max(this.fullscreenEditorMinHeight, availableHeight);
  }

  // Retorna o editor onde anotações/cursor são aplicados:
  // standalone → source editor; diff → editor modificado (lado direito).
  private getTargetEditor(): any {
    if (this.isStandalone) {
      return (this.sourceEditorRef as any)?.editor ?? null;
    }
    const diffEditor = (this.diffEditorRef as any)?.editor;
    if (!diffEditor) { return null; }
    return diffEditor.getModifiedEditor?.() ?? null;
  }

  // ─── Opções Monaco idênticas ao VS Code diff editor ───────────────────────

  applyMonacoOptions(): void {
    try {
      this.ensureCustomMonacoLanguages();

      const sharedOptions = {
        fontSize: 14,
        lineHeight: 22,
        fontFamily: "Consolas, Menlo, 'DejaVu Sans Mono', 'Liberation Mono', 'Courier New', monospace",
        fontLigatures: false,
        lineNumbers: 'on' as const,
        lineNumbersMinChars: 3,
        lineDecorationsWidth: 10,
        renderWhitespace: 'selection' as const,
        minimap: { enabled: true, scale: 1 },
        scrollbar: {
          vertical: 'visible' as const,
          horizontal: 'visible' as const,
          useShadows: true,
          verticalScrollbarSize: 14,
          horizontalScrollbarSize: 14,
        },
        overviewRulerLanes: 3,
        glyphMargin: true,         // necessário para gutter decorations
        smoothScrolling: true,
        mouseWheelZoom: false,
        scrollBeyondLastLine: false,
      };

      // Standalone: editor simples (não diff), igual VS Code que abre só o source
      if (this.isStandalone) {
        const srcEditor = (this.sourceEditorRef as any)?.editor;
        if (srcEditor) {
          srcEditor.updateOptions({
            ...sharedOptions,
            readOnly: true
          });
          const monacoGlobal = (window as any).monaco;
          const srcModel = srcEditor.getModel?.();
          if (monacoGlobal && srcModel) {
            monacoGlobal.editor.setModelLanguage(srcModel, this.sourceLanguage);
          }
        }
        return;
      }

      const monacoEditor = (this.diffEditorRef as any)?.editor;
      if (!monacoEditor) { return; }

      // Opções do diff editor
      monacoEditor.updateOptions({
        ...sharedOptions,
        renderSideBySide: !this.diffInline,
        renderIndicators: true,
        ignoreTrimWhitespace: false,
        originalEditable: false,
        enableSplitViewResizing: true
      });

      // Força layout após mudança de opções e corrige linguagem explicitamente
      // (po-code-editor pode criar o modelo antes do p-language ser aplicado pelo Angular)
      timer(50).pipe(takeUntil(this.destroy$)).subscribe(() => {
        try {
          monacoEditor.layout?.();
          const originalEditor = monacoEditor.getOriginalEditor?.();
          const modifiedEditor = monacoEditor.getModifiedEditor?.();
          originalEditor?.updateOptions?.(sharedOptions);
          modifiedEditor?.updateOptions?.(sharedOptions);
          originalEditor?.layout?.();
          modifiedEditor?.layout?.();
          const monacoGlobal = (window as any).monaco;
          if (monacoGlobal) {
            const origModel = originalEditor?.getModel?.();
            const modModel = modifiedEditor?.getModel?.();
            if (origModel) { monacoGlobal.editor.setModelLanguage(origModel, this.diffPair.language); }
            if (modModel) { monacoGlobal.editor.setModelLanguage(modModel, this.diffPair.language); }
          }
        } catch { /* Monaco não acessível */ }
      });
    } catch { /* Monaco não acessível */ }
  }

  private ensureCustomMonacoLanguages(): void {
    if (typeof window === 'undefined') { return; }
    ensureAdvplLanguageRegistered((window as any).monaco);
  }

  // ─── Sinalização inline nativa do Monaco (markers/decorations) ───────────────

  applyMonacoAnnotations(): void {
    try {
      const targetEditor = this.getTargetEditor();
      if (!targetEditor) { return; }
      this.clearMonacoAnnotations(targetEditor);
      if (!this.answer) { return; }
      const annotations = this.buildAnnotations();
      if (!annotations.length) { return; }
      this.applyGutterDecorations(targetEditor, annotations);
    } catch {
      // Monaco não acessível
    }
  }

  private clearMonacoAnnotations(editor: any): void {
    if (this.monacoDecorationIds.length) {
      editor.deltaDecorations(this.monacoDecorationIds, []);
      this.monacoDecorationIds = [];
    }
  }

  private buildAnnotations(): MonacoAnnotation[] {
    const result: MonacoAnnotation[] = [];
    if (!this.answer) { return result; }

    const parseLine = (value: unknown): number | null => {
      if (typeof value === 'number' && Number.isFinite(value)) { return value; }
      const parsed = parseInt((value ?? '').toString(), 10);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const buildHover = (title: string, details: Array<string | undefined>): string => {
      const lines = [title, ...details.filter((line): line is string => !!line && !!line.trim())];
      return lines.join('\n');
    };

    for (const ra of this.answer.required_actions ?? []) {
      const raw: any = ra;
      const line = parseLine(raw?.line ?? raw?.line_number ?? raw?.lineNumber);
      if (line == null || line < 1) { continue; }
      const sev = raw?.severity ?? raw?.level ?? raw?.priority;
      const sonarSev = formatSonarSeverity(raw?.sonar_severity);
      const sonarType = formatSonarIssueType(raw?.sonar_issue_type);
      result.push({
        line,
        severity: severityKey(sev),
        kind: 'required',
        hoverMessage: buildHover('Ação necessária', [
          sev ? `Severidade: ${formatSeverityLabel(sev)}` : undefined,
          raw?.sonar_rule_code ? `Regra Sonar: ${raw.sonar_rule_code}` : undefined,
          sonarSev ? `Severidade Sonar: ${sonarSev}` : undefined,
          sonarType ? `Tipo Sonar: ${sonarType}` : undefined
        ])
      });
    }

    for (const sg of this.answer.suggestions ?? []) {
      const raw: any = sg;
      const line = parseLine(raw?.line ?? raw?.line_number ?? raw?.lineNumber);
      if (line == null || line < 1) { continue; }
      result.push({
        line,
        severity: 'low',
        kind: 'suggestion',
        hoverMessage: buildHover('Sugestão', [
          raw?.category ? `Categoria: ${toTitle(raw.category)}` : undefined
        ])
      });
    }

    const severityRank = (s: MonacoAnnotation['severity']) => s === 'high' ? 1 : s === 'medium' ? 2 : 3;
    return result.sort((a, b) => {
      if (a.line !== b.line) { return a.line - b.line; }
      const sevDelta = severityRank(a.severity) - severityRank(b.severity);
      if (sevDelta !== 0) { return sevDelta; }
      if (a.kind !== b.kind) { return a.kind === 'required' ? -1 : 1; }
      return 0;
    });
  }

  private applyGutterDecorations(editor: any, annotations: MonacoAnnotation[]): void {
    const newDecorations = annotations.map(ann => ({
      range: { startLineNumber: ann.line, startColumn: 1, endLineNumber: ann.line, endColumn: 1 },
      options: {
        isWholeLine: true,
        glyphMarginClassName: `sr-gutter-${ann.severity}`,
        glyphMarginHoverMessage: ann.hoverMessage ? { value: ann.hoverMessage } : undefined,
        className: `sr-line-highlight-${ann.severity}`,
        overviewRuler: {
          color: ann.severity === 'high' ? '#f44747' : ann.severity === 'medium' ? '#ffcc00' : '#4aa3ff',
          position: 4 /* OverviewRulerLane.Right */
        }
      }
    }));
    this.monacoDecorationIds = editor.deltaDecorations([], newDecorations);
  }

  private bindCursorTracking(): void {
    try {
      this.cursorChangeDisposable?.dispose?.();
      this.cursorChangeDisposable = null;

      const targetEditor = this.getTargetEditor();
      if (!targetEditor?.onDidChangeCursorPosition) { return; }

      this.cursorChangeDisposable = targetEditor.onDidChangeCursorPosition((e: any) => {
        const line = e?.position?.lineNumber;
        if (typeof line !== 'number' || line < 1) { return; }
        this.zone.run(() => {
          this.syncActiveAnnotationLine(line);
          if (!this.isStandalone && this.diffNavigatorActivated) {
            this.syncDiffIndexFromCursor();
          }
          this.cdr.markForCheck();
        });
      });
    } catch { /* */ }
  }

  private bindMouseTracking(): void {
    try {
      this.mouseDownDisposable?.dispose?.();
      this.mouseDownDisposable = null;

      const targetEditor = this.getTargetEditor();
      const bindMouseDown = typeof targetEditor?.onMouseDown === 'function'
        ? targetEditor.onMouseDown.bind(targetEditor)
        : null;
      if (!bindMouseDown) { return; }

      this.mouseDownDisposable = bindMouseDown((event: any) => {
        const line = event?.target?.position?.lineNumber ?? targetEditor?.getPosition?.()?.lineNumber;
        if (typeof line !== 'number' || line < 1) { return; }
        this.zone.run(() => {
          this.syncActiveAnnotationLine(line);
          this.cdr.markForCheck();
        });
      });
    } catch { /* */ }
  }

  private syncActiveAnnotationLine(line: number): void {
    const lines = [
      ...(this.answer?.required_actions ?? []).map(a => a.line).filter((n): n is number => typeof n === 'number' && n > 0),
      ...(this.answer?.suggestions ?? []).map(s => s.line).filter((n): n is number => typeof n === 'number' && n > 0)
    ];
    if (!lines.length) {
      this.setActiveAnnotationLine(null);
      return;
    }

    if (lines.includes(line)) {
      this.setActiveAnnotationLine(line);
      return;
    }

    let nearest = lines[0];
    let minDelta = Math.abs(lines[0] - line);
    for (const candidate of lines) {
      const delta = Math.abs(candidate - line);
      if (delta < minDelta) {
        minDelta = delta;
        nearest = candidate;
      }
    }
    this.setActiveAnnotationLine(minDelta <= 3 ? nearest : null);
  }


  // ─── Diff Navigator (prev/next change) ───────────────────────────────────

  initDiffNavigator(): void {
    if (this.isStandalone) {
      this.diffChangeCount = 0;
      this.diffChangeIndex = 0;
      this.diffNavigatorActivated = false;
      return;
    }
    const changes = this.getDiffNavigationChanges();
    this.diffChangeCount = changes.length;
    this.diffChangeIndex = 0;
    this.diffNavigatorActivated = false;
  }

  private getCurrentModifiedLine(): number {
    try {
      const monacoEditor = (this.diffEditorRef as any)?.editor;
      return monacoEditor?.getModifiedEditor?.()?.getPosition?.()?.lineNumber ?? 0;
    } catch { return 0; }
  }

  private revealDiffChange(change: any): void {
    try {
      const monacoEditor = (this.diffEditorRef as any)?.editor;
      const mod = monacoEditor?.getModifiedEditor?.();
      if (!mod) { return; }
      const startLine = this.getChangeStartLine(change);
      const endLine = this.getChangeEndLine(change);
      mod.revealLinesInCenter(startLine, endLine);
      mod.setPosition({ lineNumber: startLine, column: 1 });
      mod.focus();
    } catch { /* */ }
  }

  nextDiff(): void {
    try {
      const changes = this.getDiffNavigationChanges();
      if (!changes.length) { return; }
      this.diffNavigatorActivated = true;
      const cur = this.getCurrentModifiedLine();
      const idx = changes.findIndex(change => this.getChangeStartLine(change) > cur);
      const next = idx >= 0 ? idx : 0; // volta ao início ao chegar no fim
      this.diffChangeIndex = next + 1;
      this.revealDiffChange(changes[next]);
    } catch { /* */ }
  }

  prevDiff(): void {
    try {
      const changes = this.getDiffNavigationChanges();
      if (!changes.length) { return; }
      this.diffNavigatorActivated = true;
      const cur = this.getCurrentModifiedLine();
      let idx = -1;
      for (let i = changes.length - 1; i >= 0; i--) {
        if (this.getChangeStartLine(changes[i]) < cur) { idx = i; break; }
      }
      const prev = idx >= 0 ? idx : changes.length - 1; // vai para o último ao chegar no início
      this.diffChangeIndex = prev + 1;
      this.revealDiffChange(changes[prev]);
    } catch { /* */ }
  }

  // ─── Modo Diff: inline ↔ lado a lado ──────────────────────────────────────

  private refreshResponsiveDiffMode(): void {
    if (this.isStandalone) {
      this.canUseSideBySide = true;
      this.forcedInlineByViewport = false;
      return;
    }

    const nextCanUse = this.getCurrentLayoutWidth() >= this.sideBySideMinWidth;
    const changed = this.canUseSideBySide !== nextCanUse;
    this.canUseSideBySide = nextCanUse;

    if (!this.canUseSideBySide) {
      if (!this.diffInline) {
        this.forcedInlineByViewport = true;
        this.setDiffModeInternal(true, false);
      } else if (changed) {
        this.forcedInlineByViewport = false;
      }
      return;
    }

    if (changed && this.forcedInlineByViewport) {
      this.forcedInlineByViewport = false;
      this.setDiffModeInternal(this.preferredDesktopInline, false);
    }
  }

  private applyDiffMode(): void {
    try {
      const monacoEditor = (this.diffEditorRef as any)?.editor;
      if (monacoEditor) {
        monacoEditor.updateOptions({ renderSideBySide: !this.diffInline });
        return;
      }
      const diffEditors = (window as any).monaco?.editor?.getDiffEditors?.();
      if (diffEditors?.length) {
        diffEditors[diffEditors.length - 1].updateOptions({ renderSideBySide: !this.diffInline });
      }
    } catch { /* Monaco não acessível */ }
  }

  private setDiffModeInternal(inline: boolean, updateDesktopPreference: boolean): void {
    const nextInline = (!this.canUseSideBySide && !inline) ? true : inline;
    if (updateDesktopPreference && this.canUseSideBySide) {
      this.preferredDesktopInline = nextInline;
    }
    if (this.diffInline === nextInline) { return; }
    this.diffInline = nextInline;
    if (this.viewReady) {
      this.applyDiffMode();
      this.scheduleEditorReflow();
    }
  }

  setDiffMode(inline: boolean): void {
    this.setDiffModeInternal(inline, true);
  }

  private syncDiffIndexFromCursor(): void {
    const changes = this.getDiffNavigationChanges();
    this.diffChangeCount = changes.length;
    if (!changes.length) {
      this.diffChangeIndex = 0;
      return;
    }
    const line = this.getCurrentModifiedLine();
    if (!line) {
      this.diffChangeIndex = 1;
      return;
    }
    const insideIdx = changes.findIndex(change => {
      const start = this.getChangeStartLine(change);
      const end = this.getChangeEndLine(change);
      return line >= start && line <= end;
    });
    if (insideIdx >= 0) {
      this.diffChangeIndex = insideIdx + 1;
      return;
    }

    const nextIdx = changes.findIndex(change => this.getChangeStartLine(change) > line);
    this.diffChangeIndex = (nextIdx >= 0 ? nextIdx : changes.length - 1) + 1;
  }

  private getDiffNavigationChanges(): any[] {
    if (this.isStandalone) { return []; }

    try {
      const monacoEditor = (this.diffEditorRef as any)?.editor;
      const lineChanges = monacoEditor?.getLineChanges?.();
      if (Array.isArray(lineChanges) && lineChanges.length) {
        return lineChanges;
      }
    } catch {
      // fallback abaixo
    }

    return this.diffPair.hunks ?? [];
  }

  private getChangeStartLine(change: any): number {
    const line = Number(change?.modifiedStartLineNumber)
      || Number(change?.modifiedEndLineNumber)
      || Number(change?.originalStartLineNumber)
      || Number(change?.originalEndLineNumber)
      || 1;
    return Math.max(1, line);
  }

  private getChangeEndLine(change: any): number {
    const start = this.getChangeStartLine(change);
    const end = Number(change?.modifiedEndLineNumber)
      || Number(change?.modifiedStartLineNumber)
      || Number(change?.originalEndLineNumber)
      || Number(change?.originalStartLineNumber)
      || start;
    return Math.max(start, end);
  }

  private scheduleEditorReflow(): void {
    if (this.loading) { return; }
    const generation = ++this.reflowGeneration;
    if (this.resizeReflowTimer) {
      clearTimeout(this.resizeReflowTimer);
      this.resizeReflowTimer = null;
    }
    this.clearDeferredAnnotationTimers();
    this.queueEditorReflow(generation, this.initialReflowDelayMs, 0);
  }

  private queueEditorReflow(generation: number, delayMs: number, retryCount: number): void {
    this.resizeReflowTimer = setTimeout(() => {
      this.resizeReflowTimer = null;
      if (generation !== this.reflowGeneration || this.loading) { return; }

      this.applyMonacoOptions();
      this.initDiffNavigator();
      this.applyMonacoAnnotations();
      this.bindCursorTracking();
      this.bindMouseTracking();
      this.forceEditorLayout();
      this.syncSidePanelHeight();
      this.scheduleDeferredAnnotationPasses(generation);
      this.cdr.markForCheck();

      if (retryCount >= this.maxReflowRetries || this.isReflowReadyToStopRetry()) {
        return;
      }

      this.queueEditorReflow(generation, this.reflowRetryDelayMs, retryCount + 1);
    }, delayMs);
  }

  private isEditorReadyForAnnotations(): boolean {
    if (this.isStandalone) {
      return !!(this.sourceEditorRef as any)?.editor;
    }
    const diffEditor = (this.diffEditorRef as any)?.editor;
    return !!diffEditor?.getModifiedEditor?.();
  }

  private isReflowReadyToStopRetry(): boolean {
    if (!this.isEditorReadyForAnnotations()) { return false; }
    return !this.hasExpectedAnnotationsWithoutDecorations();
  }

  private hasExpectedAnnotationsWithoutDecorations(): boolean {
    if (!this.answer) { return false; }
    try {
      const expected = this.buildAnnotations().length;
      return expected > 0 && this.monacoDecorationIds.length === 0;
    } catch {
      return false;
    }
  }

  private scheduleDeferredAnnotationPasses(generation: number): void {
    const delays = [280, 620];
    for (const delay of delays) {
      const timerId = setTimeout(() => {
        if (generation !== this.reflowGeneration || this.loading) { return; }
        this.applyMonacoAnnotations();
        this.bindCursorTracking();
        this.bindMouseTracking();
        this.syncSidePanelHeight();
        this.cdr.markForCheck();
      }, delay);
      this.deferredAnnotationTimers.push(timerId);
    }
  }

  private clearDeferredAnnotationTimers(): void {
    for (const timerId of this.deferredAnnotationTimers) {
      clearTimeout(timerId);
    }
    this.deferredAnnotationTimers = [];
  }

  private forceEditorLayout(): void {
    try {
      if (this.isStandalone) {
        const srcEditor = (this.sourceEditorRef as any)?.editor;
        if (srcEditor?.layout) {
          srcEditor.layout();
        }
      } else {
        const monacoEditor = (this.diffEditorRef as any)?.editor;
        if (monacoEditor?.layout) {
          monacoEditor.layout();
        }
        const originalEditor = monacoEditor?.getOriginalEditor?.();
        const modifiedEditor = monacoEditor?.getModifiedEditor?.();
        if (originalEditor?.layout) { originalEditor.layout(); }
        if (modifiedEditor?.layout) { modifiedEditor.layout(); }
      }
    } catch { /* Monaco não acessível */ }
  }

  private observeContainerResize(): void {
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') { return; }
    const host = this.hostRef?.nativeElement;
    if (!host) { return; }

    this.lastContainerWidth = Math.round(host.getBoundingClientRect().width);
    this.containerResizeObserver?.disconnect();
    this.containerResizeObserver = new ResizeObserver(entries => {
      const width = Math.round(entries[0]?.contentRect?.width ?? 0);
      if (!width || width === this.lastContainerWidth) { return; }
      this.lastContainerWidth = width;
      this.zone.run(() => {
        this.refreshResponsiveDiffMode();
        this.refreshToolbarDensity(width);
        this.scheduleEditorReflow();
      });
    });

    this.containerResizeObserver.observe(host);
  }

  private getCurrentLayoutWidth(): number {
    const hostWidth = Math.round(this.hostRef?.nativeElement?.getBoundingClientRect?.().width ?? 0);
    if (hostWidth > 0) { return hostWidth; }
    if (typeof window !== 'undefined') { return window.innerWidth; }
    return this.sideBySideMinWidth;
  }

  private refreshToolbarDensity(layoutWidth?: number): void {
    const width = layoutWidth && layoutWidth > 0 ? layoutWidth : this.getCurrentLayoutWidth();
    const hasSidePanel = this.showFindingsPanel;
    const compactThreshold = hasSidePanel
      ? this.toolbarCompactWithSideThreshold
      : this.toolbarCompactThreshold;
    const ultraCompactThreshold = hasSidePanel
      ? this.toolbarUltraCompactWithSideThreshold
      : this.toolbarUltraCompactThreshold;

    const nextCompact = width <= compactThreshold;
    const nextUltraCompact = width <= ultraCompactThreshold;

    if (this.toolbarCompact === nextCompact && this.toolbarUltraCompact === nextUltraCompact) {
      return;
    }

    this.toolbarCompact = nextCompact;
    this.toolbarUltraCompact = nextUltraCompact;
    if (this.viewReady) {
      this.cdr.markForCheck();
    }
  }

  get annotationsPanelHeight(): number {
    return this.sidePanelHeight > 0 ? this.sidePanelHeight : this.diffHeight;
  }

  private syncSidePanelHeight(): void {
    if (!this.viewReady) { return; }
    const surface = this.hostRef?.nativeElement?.querySelector('.sr-diff-main .sr-diff-surface') as HTMLElement | null;
    if (!surface) { return; }

    const nextHeight = Math.round(surface.getBoundingClientRect().height);
    if (!Number.isFinite(nextHeight) || nextHeight <= 0 || this.sidePanelHeight === nextHeight) { return; }

    this.sidePanelHeight = nextHeight;
  }

  // ─── Painel de Anotações — scroll Monaco ───────────────────────────────────

  onAnnotationClicked(line: number): void {
    this.setActiveAnnotationLine(line);
    try {
      const targetEditor = this.getTargetEditor();
      if (!targetEditor) { return; }
      targetEditor.revealLineInCenter(line);
      targetEditor.setPosition({ lineNumber: line, column: 1 });
      targetEditor.focus();
    } catch { /* Monaco não acessível */ }
  }

}
